import React from 'react';
import { fmtDate } from '../lib/dates';
import { fmtBudgetRange } from '../lib/budget';
import { formatGBP } from '../lib/money';
import { isOverdue } from '../lib/briefStatus';

// Brief-detail sticky facts rail (Look & Feel refactor, Phase 2).
//
// The page header is pinned (status + title + actions never scroll), so this
// rail deliberately does NOT repeat actions. It carries the FACTS that
// otherwise scroll out of view exactly when you're deep in the recipients
// table or comments: deadline, budget, key dates, response tally, and the
// award outcome. Status-aware — the sections shown adapt to the brief's state.
//
// Purely presentational: every value + count is passed in from BriefDetail so
// there is one source of truth. No data fetching, no handlers of its own
// beyond the optional scroll callbacks.

// Relative deadline phrasing. Whole-day granularity is enough for a facts
// summary — an exact "14h" would imply a precision the rest of the page
// doesn't. Returns null when there's no deadline to describe.
function deadlineHint(deadlineIso, live) {
  if (!deadlineIso || !live) return null;
  const deadline = new Date(deadlineIso);
  if (Number.isNaN(deadline.getTime())) return null;
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  // Compare calendar days so "today" reads as today right up to midnight.
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(deadline) - startOfDay(now)) / dayMs);
  if (days < 0) return { text: `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`, tone: 'risk' };
  if (days === 0) return { text: 'Due today', tone: 'attention' };
  if (days === 1) return { text: 'Due tomorrow', tone: 'attention' };
  if (days <= 7) return { text: `${days} days left`, tone: 'progress' };
  return { text: `${days} days left`, tone: 'neutral' };
}

const HINT_TONE = {
  risk: 'text-[#E0290A]',
  attention: 'text-[#7A4E10]',
  progress: 'text-fusion-teal',
  neutral: 'text-[#51534A]',
};

function Row({ label, children }) {
  return (
    <div className="py-3 border-b border-line last:border-0">
      <p className="fusion-field-label">{label}</p>
      <div className="font-['Metropolis'] text-sm text-ink">{children}</div>
    </div>
  );
}

export default function BriefFactsRail({
  brief,
  statusPill,
  counts = {},
  onJumpToRecipients,
  onJumpToComments,
  onJumpToQuestions,
  onJumpToUpdates,
  unansweredQuestions = 0,
  oldestUnansweredDays = null,
  deadlineDaysOverdue = null,   // days past deadline with no outcome (§3)
  quotesIn = 0,                 // responses in, for the "no decision recorded" line
  updateRequestCount = 0,       // suppliers who asked for a post-deadline update
  updateRequestNote = null,     // a hold-until snippet
  commentCount = 0,
  // { complete, outstanding, outstandingLabels, by: [{ what, who }] } — the
  // close-down state, built by the caller from lib/briefClosedown so the rail
  // and the card on the page can't disagree.
  closedown = null,
}) {
  if (!brief) return null;

  const status = brief.status;
  const isDraft = status === 'draft';
  const live = status === 'sent';
  const isAwarded = status === 'closed' && !!brief.awarded_supplier_id;
  const isClosed = status === 'closed' && !brief.awarded_supplier_id;

  const deadline = brief.required_response_time;
  const overdue = isOverdue(status, deadline);
  const hint = deadlineHint(deadline, live);

  const budget = fmtBudgetRange(brief, { withVat: true });
  const eventDate = brief.event_live_date && !brief.event_live_date_na ? fmtDate(brief.event_live_date) : null;
  // Access window — expected_required_by_date is "from", delivery_access_to_date
  // is the optional "to". Renders as a range when both are set.
  const accessWindow = brief.expected_required_by_date && !brief.delivery_date_na
    ? (brief.delivery_access_to_date
        ? `${fmtDate(brief.expected_required_by_date)} – ${fmtDate(brief.delivery_access_to_date)}`
        : fmtDate(brief.expected_required_by_date))
    : null;

  const total = counts.total ?? 0;
  const responded = counts.responded ?? 0;
  const declined = counts.declined ?? 0;
  // "Yet to respond" = outstanding (pending + accepted-not-submitted), derived
  // once in briefResponseCounts so the rail reconciles with the cards + log.
  const outstanding = counts.outstanding ?? 0;
  // "Needs you" (supplier-questions §6 rail + post-deadline §3) — the things
  // awaiting the owner on a live brief. Only rendered when non-empty.
  const overdueNoOutcome = deadlineDaysOverdue != null && deadlineDaysOverdue >= 1;
  const needsYou = live && (overdueNoOutcome || updateRequestCount > 0 || unansweredQuestions > 0 || outstanding > 0);
  const awardName = brief.awarded_supplier?.supplier_name;
  const awardAmount = brief.awarded_amount != null && brief.awarded_amount !== ''
    ? formatGBP(brief.awarded_amount)
    : null;

  return (
    <div className="bg-white border border-line rounded-xl p-4">
      {/* Status — restated compactly. The header pins the full pill row, but
          repeating the single current state here keeps the rail legible on
          its own while scrolled far down the page. */}
      {statusPill && (
        <div className="pb-3 border-b border-line">
          <span className={`inline-flex items-center font-['Metropolis'] text-[11px] font-bold tracking-[0.1em] uppercase rounded-full px-3 py-1 ${statusPill.className}`}>
            {statusPill.label}
          </span>
        </div>
      )}

      {/* Needs you — sticky-worthy call to action on a live brief. Renders
          only when non-empty (§6): unanswered questions first (a waiting
          question has a growing cost), then suppliers yet to respond. */}
      {needsYou && (
        <div className="py-3 border-b border-line">
          <p className="fusion-field-label text-[#7A4E10]">Needs you</p>
          <div className="mt-1 space-y-1.5">
            {/* Deadline passed, no outcome — the internal flag that usually
                clears the whole thing before a supplier has to ask (§3). */}
            {overdueNoOutcome && (
              <button
                type="button"
                onClick={onJumpToRecipients}
                className="block w-full text-left font-['Metropolis'] text-sm text-[#E0290A] hover:text-fusion-forest transition-colors">
                <span className="font-semibold">Deadline passed {deadlineDaysOverdue} day{deadlineDaysOverdue === 1 ? '' : 's'} ago</span>
                <span className="block text-xs text-[#51534A]">{quotesIn > 0 ? `${quotesIn} quote${quotesIn === 1 ? '' : 's'} in, no decision recorded` : 'No decision recorded'}</span>
              </button>
            )}
            {updateRequestCount > 0 && (
              <button
                type="button"
                onClick={onJumpToUpdates || onJumpToRecipients}
                className="block w-full text-left font-['Metropolis'] text-sm text-ink hover:text-fusion-forest transition-colors">
                <span className="font-semibold">{updateRequestCount} supplier{updateRequestCount === 1 ? '' : 's'} asked for an update</span>
                {updateRequestNote && <span className="block text-xs text-[#51534A] truncate" title={updateRequestNote}>{updateRequestNote}</span>}
              </button>
            )}
            {unansweredQuestions > 0 && (
              <button
                type="button"
                onClick={onJumpToQuestions}
                className="block w-full text-left font-['Metropolis'] text-sm text-ink hover:text-fusion-forest transition-colors">
                <span className="font-semibold">{unansweredQuestions}</span> question{unansweredQuestions === 1 ? '' : 's'} unanswered
                {oldestUnansweredDays != null && (
                  <span className="text-[#51534A]"> · oldest waiting {oldestUnansweredDays} day{oldestUnansweredDays === 1 ? '' : 's'}</span>
                )}
              </button>
            )}
            {outstanding > 0 && (
              <button
                type="button"
                onClick={onJumpToRecipients}
                className="block w-full text-left font-['Metropolis'] text-sm text-ink hover:text-fusion-forest transition-colors">
                <span className="font-semibold">{outstanding}</span> supplier{outstanding === 1 ? '' : 's'} yet to respond
              </button>
            )}
          </div>
        </div>
      )}

      {/* The deadline DATE lives in the body (restructure §1 assigns it
          there), so the rail carries only the part the body can't: how long
          is left, and whether that's a problem. "3 days left" is the fact
          worth having pinned while you read; the date itself is reference.
          Hidden once the outcome is settled — deadline proximity is noise on
          a brief that's already decided. */}
      {deadline && hint && !isAwarded && !isClosed && (
        <Row label="Time left">
          <span className={`font-semibold ${HINT_TONE[hint.tone]}`}>{hint.text}</span>
          {overdue && <span className="block mt-0.5 text-xs text-[#51534A]">Deadline was {fmtDate(deadline)}</span>}
        </Row>
      )}

      {/* Questions close — a deadline in its own right (§6 rail), and one the
          body doesn't carry, so it isn't a duplicate. */}
      {brief.questions_close && !isAwarded && !isClosed && (
        <Row label="Questions close">{fmtDate(brief.questions_close)}</Row>
      )}

      {isDraft && (
        <Row label="Visibility">
          <span className="text-fusion-teal font-semibold">Draft — not yet sent</span>
        </Row>
      )}

      {budget && <Row label="Budget">{budget}</Row>}
      {eventDate && <Row label="Event date">{eventDate}</Row>}
      {accessWindow && <Row label="Access">{accessWindow}</Row>}

      {/* Response tally — only meaningful once suppliers can respond. */}
      {(live || isAwarded || isClosed) && total > 0 && (
        <Row label="Responses">
          <button
            type="button"
            onClick={onJumpToRecipients}
            className="text-left hover:text-fusion-forest transition-colors"
            title="Jump to recipients">
            <span className="font-semibold">{responded}</span> of {total} responded
            {declined > 0 && <span className="text-[#51534A]"> · {declined} declined</span>}
          </button>
        </Row>
      )}

      {/* Award outcome — the headline fact on a decided brief. */}
      {isAwarded && awardName && (
        <Row label="Awarded to">
          <span className="text-fusion-forest font-semibold">{awardName}</span>
          {awardAmount && <span className="block mt-0.5 text-xs text-[#51534A]">Final cost {awardAmount}</span>}
        </Row>
      )}

      {/* Close-down — the rail is where people look to see where a brief has
          got to, so its state belongs here rather than only inside the card
          further down the page. Named, because "who signed this off?" is the
          question asked when a figure is queried later. */}
      {closedown && (
        <Row label="Close-down">
          {closedown.complete ? (
            <>
              <span className="text-fusion-forest font-semibold">Complete</span>
              {closedown.by.length > 0 && (
                <span className="block mt-0.5 text-xs text-[#51534A]">
                  {closedown.by.map(p => `${p.what} by ${p.who}`).join(' · ')}
                </span>
              )}
            </>
          ) : (
            <>
              <span className="text-[#7A4E10] font-semibold">{closedown.outstanding} outstanding</span>
              <span className="block mt-0.5 text-xs text-[#51534A]">{closedown.outstandingLabels.join(' · ')}</span>
            </>
          )}
        </Row>
      )}

      {commentCount > 0 && (
        <Row label="Discussion">
          <button
            type="button"
            onClick={onJumpToComments}
            className="text-left hover:text-fusion-forest transition-colors"
            title="Jump to comments">
            {commentCount} comment{commentCount === 1 ? '' : 's'}
          </button>
        </Row>
      )}
    </div>
  );
}
